import { GroupOptionToggle, SearchFilterField, SideFilters } from '@retail/components';
import { useAppTFunction } from '@retail/app/i18n';
import { CampaignTypeToggleGroup } from './CampaignTypeToggleGroup';
import { CampaignDatePreset, CampaignFilterType, CampaignType } from '@retail/campaign/types';
import { Box } from '@mui/material';
import { Brand } from '@retail/auth/types';
import { SelectBrandFilter } from './SelectBrandFilter';
import { useCallback, useMemo } from 'react';
import { CampaignDatePresetFilter } from './CampaignDatePresetFilter';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { GroupOption } from '@retail/products/types';

export type CampaignTypeOption = CampaignType | 'ALL';

export interface CampaignPageFilterProps {
  selectedDatePreset: CampaignDatePreset;
  selectDatePreset: (preset: CampaignDatePreset) => void;
  searchTerm: string;
  mandatoryFilter: boolean | null;
  campaignTypeFilter: CampaignTypeOption;
  brandFilters: string[];
  campaignFromDateFilter: Date | null;
  campaignToDateFilter: Date | null;
  setSearchTerm: (q: string) => void;
  setMandatoryFilter: (isMandatory: boolean | null) => void;
  setBrandFilters: (brandIds: string[]) => void;
  setCampaignTypeFilter: (type: CampaignTypeOption) => void;
  setCampaignFromDateFilter: (date: Date | null) => void;
  setCampaignToDateFilter: (date: Date | null) => void;
  campaignTypeOptions: CampaignTypeOption[];
  availableFilters: CampaignFilterType[];
}

interface Props extends CampaignPageFilterProps {
  brands: Brand[];
  disableFields: boolean;
  showFilters: boolean;
}

const toGroupOption = (isMandatory: boolean | null): GroupOption =>
  isMandatory === null ? 'UNSPECIFIED' : isMandatory ? 'YES' : 'NO';

export function CampaignFilters({
  selectedDatePreset,
  selectDatePreset,
  brands,
  searchTerm,
  disableFields,
  showFilters,
  mandatoryFilter,
  brandFilters,
  campaignTypeFilter,
  setSearchTerm,
  setBrandFilters,
  setMandatoryFilter,
  setCampaignTypeFilter,
  campaignTypeOptions,
  availableFilters,
}: Props) {
  const t = useAppTFunction();
  const tCampaign = useCampaignTFunction();

  const { showCampaignType, showMandatory, showBrands, showSearch } = useMemo(
    () => ({
      showCampaignType: availableFilters.includes('campaignType'),
      showMandatory: availableFilters.includes('mandatory'),
      showBrands: availableFilters.includes('brands'),
      showSearch: availableFilters.includes('search'),
    }),
    [availableFilters]
  );

  const setMandatoryFromGroupOption = useCallback(
    (option: GroupOption) => setMandatoryFilter(option === 'UNSPECIFIED' ? null : option === 'YES'),
    [setMandatoryFilter]
  );

  return (
    <SideFilters showFilters={showFilters} sx={{ py: 1, height: '100%' }}>
      <CampaignDatePresetFilter
        selectedPreset={selectedDatePreset}
        selectPreset={selectDatePreset}
      />
      {showMandatory && (
        <Box mt={1}>
          <GroupOptionToggle
            label={tCampaign('campaign.filters.mandatory.label')}
            value={toGroupOption(mandatoryFilter)}
            onChange={setMandatoryFromGroupOption}
            allLabel={tCampaign('campaign.filters.mandatory.all')}
            yesLabel={tCampaign('campaign.filters.mandatory.yes')}
            noLabel={tCampaign('campaign.filters.mandatory.no')}
            fullWidth={false}
          />
        </Box>
      )}
      {showCampaignType && (
        <CampaignTypeToggleGroup
          currentCampaignType={campaignTypeFilter}
          setCampaignType={setCampaignTypeFilter}
          options={campaignTypeOptions}
        />
      )}
      {showBrands && (
        <SelectBrandFilter
          brands={brands}
          selectedBrands={brandFilters}
          selectBrands={setBrandFilters}
        />
      )}
      {showSearch && (
        <SearchFilterField
          label={t('campaign.campaignSearch')}
          placeholder={t('campaign.campaignSearchInfo')}
          size="small"
          width="100%"
          searchTerm={searchTerm}
          isDisabled={disableFields}
          onSubmitSearch={setSearchTerm}
        />
      )}
    </SideFilters>
  );
}
